import {
    SearchRequestOrderingEnum,
    PeopleSearchRequestDataOrderingEnum, DiscussionsSearchRequestDataOrderingEnum,
    OrgSearchRequestOrderingEnum,
} from 'labxchange-client';
import {
    SORT_OPTIONS_LAST_ADDED,
    SORT_OPTIONS_MOST_FAVORITED,
    SORT_OPTIONS_MOST_LIKED,
    SORT_OPTIONS_MOST_RECENT,
    SORT_OPTIONS_MOST_REMIXED,
    SORT_OPTIONS_MOST_VIEWED,
    SORT_OPTIONS_RECENTLY_JOINED,
    SORT_OPTIONS_RELEVANCE
} from './locales-constants';
import messages from './displayMessages';
import { locale, intl } from 'i18n';
import { AuthProviderEnum } from 'auth/utils';

export enum EnableLxLoginEnum {
    None = 'none', // Use the old auth views
    Debug = 'debug', // The old auth logic is enable, but you can access to /lx-sign-in and /lx-sign-up
    Production = 'production', // Overwrite the old auth with the new lx auth views and logic
}

// Declare our globals that are set in vite.config.js and in github workflows under .github/workflows
declare const DISABLE_IN_PROGRESS_FEATURES: boolean;
declare const BACKEND_BASE_URL: string;
declare const COMMIT_SHA: string;
declare const HOMEPAGE_PROMO_VIDEO_ID: string;
declare const HOMEPAGE_PROMO_VIDEO_IMAGE_URL: string;
declare const DEPRECATED_ITEM_ALERT_URL: string;
declare const LMS_BASE_URL: string;
declare const OAUTH2_CLIENT_ID: string;
declare const ENABLE_LX_LOGIN: EnableLxLoginEnum;
declare const ENABLED_AUTH_PROVIDERS: AuthProviderEnum[];
declare const MESSAGE_POLL_DEFAULT_INTERVAL: number;
declare const NOTIFICATION_POLL_DEFAULT_INTERVAL: number;
declare const DISCUSSION_POLL_DEFAULT_INTERVAL: number;
declare const YOUTUBE_API_KEY: string;
declare const VIMEO_API_KEY: string;
declare const FRONTEND_USERSNAP_API_KEY: string;
declare const FRONTEND_USERSNAP_TRANSLATIONS_API_KEY: string;

// And export them here:

export const LxConfig = Object.freeze({
    DisableInProgressFeatures: DISABLE_IN_PROGRESS_FEATURES,
    BackendBaseUrl: BACKEND_BASE_URL,
    CommitSha: COMMIT_SHA,
    HomepagePromoVideoId: HOMEPAGE_PROMO_VIDEO_ID,
    HomepagePromoVideoImageUrl: HOMEPAGE_PROMO_VIDEO_IMAGE_URL,
    DeprecatedItemAlertUrl: DEPRECATED_ITEM_ALERT_URL,
    LmsBaseUrl: LMS_BASE_URL,
    Oauth2ClientId: OAUTH2_CLIENT_ID,
    EnableLxLogin: ENABLE_LX_LOGIN,
    EnabledAuthProviders: ENABLED_AUTH_PROVIDERS,
    // Normal message polling interval
    MessagePollDefaultInterval: MESSAGE_POLL_DEFAULT_INTERVAL,
    // Check for messages more frequently when the user is viewing the messages page
    NotificationPollDefaultInterval: NOTIFICATION_POLL_DEFAULT_INTERVAL,
    DiscussionPollDefaultInterval: DISCUSSION_POLL_DEFAULT_INTERVAL,
    YouTubeApiKey: YOUTUBE_API_KEY,
    VimeoApiKey: VIMEO_API_KEY,
    UsersnapAPIKey: FRONTEND_USERSNAP_API_KEY,
    UsersnapTranslationProjectAPIKey: FRONTEND_USERSNAP_TRANSLATIONS_API_KEY,
});

// default pagination page size
export const PAGINATION_SIZE = 24;
// allowed options for pagination sizes that the user can select
export const PAGINATION_OPTIONS = [
    { label: '24', value: 24 },
    { label: '48', value: 48 },
    { label: '96', value: 96 },
];

export const MAX_PATHWAY_ITEMS = 30;

export const MAX_IMAGE_ASSET_SIZE = 20; // 20 MB is the max value in edx. Adding more can generate errors

export const PATHWAY_COVER_IMAGE_URL = (fname: string) => `/assets/images/pathway-cover-images/${fname}`;

export const LIBRARY_SORT_OPTIONS = [
    { label: SORT_OPTIONS_RELEVANCE[locale], value: SearchRequestOrderingEnum.Relevance },
    { label: SORT_OPTIONS_LAST_ADDED[locale], value: SearchRequestOrderingEnum.LastAdded },
    { label: SORT_OPTIONS_MOST_VIEWED[locale], value: SearchRequestOrderingEnum.MostViewed },
    { label: SORT_OPTIONS_MOST_FAVORITED[locale], value: SearchRequestOrderingEnum.MostFavorited },
    { label: SORT_OPTIONS_MOST_REMIXED[locale], value: SearchRequestOrderingEnum.MostRemixed },
    { label: 'A-Z', value: SearchRequestOrderingEnum.Alphabetical },
];

export const DASHBOARD_SORT_OPTIONS = [
    { label: SORT_OPTIONS_LAST_ADDED[locale], value: SearchRequestOrderingEnum.LastAdded },
    { label: SORT_OPTIONS_MOST_VIEWED[locale], value: SearchRequestOrderingEnum.MostViewed },
    { label: SORT_OPTIONS_MOST_FAVORITED[locale], value: SearchRequestOrderingEnum.MostFavorited },
    { label: SORT_OPTIONS_MOST_REMIXED[locale], value: SearchRequestOrderingEnum.MostRemixed },
    { label: 'A-Z', value: SearchRequestOrderingEnum.Alphabetical },
];

export const PEOPLE_SORT_OPTIONS = [
    { label: SORT_OPTIONS_RELEVANCE[locale], value: PeopleSearchRequestDataOrderingEnum.Relevance },
    { label: 'A-Z', value: PeopleSearchRequestDataOrderingEnum.NameAtoz },
    { label: 'Z-A', value: PeopleSearchRequestDataOrderingEnum.NameZtoa },
    { label: SORT_OPTIONS_RECENTLY_JOINED[locale], value: PeopleSearchRequestDataOrderingEnum.RecentlyJoined },
];

export const ORG_SORT_OPTIONS = [
    { label: SORT_OPTIONS_RELEVANCE[locale], value: OrgSearchRequestOrderingEnum.Relevance },
    { label: 'A-Z', value: OrgSearchRequestOrderingEnum.Alphabetical },
    { label: intl.formatMessage(messages.popularity), value: OrgSearchRequestOrderingEnum.Popularity },
    { label: intl.formatMessage(messages.ncontent), value: OrgSearchRequestOrderingEnum.Content },
];

export const DISCUSSIONS_SORT_OPTIONS = [
    { label: SORT_OPTIONS_RELEVANCE[locale], value: DiscussionsSearchRequestDataOrderingEnum.Relevance },
    { label: SORT_OPTIONS_MOST_RECENT[locale], value: DiscussionsSearchRequestDataOrderingEnum.MostRecent },
    { label: SORT_OPTIONS_MOST_LIKED[locale], value: DiscussionsSearchRequestDataOrderingEnum.MostLiked },
];

/**
 * API paths. Note you should generally use an auto-generated API client
 * from global/api.ts instead of fetching URLs directly.
 */
export const API = {
    // Auth API Paths
    Auth: {
        OATUH2_AUTHORIZE_ENDPOINT: '/oauth2/authorize/',
        LOGOUT: `${BACKEND_BASE_URL}/api/v1/users/logout/`,
        OAUTH2_AUTHORIZE: `${BACKEND_BASE_URL}/oauth2/authorize/`,
    },
    // User API Paths
    Users: {
        AVATAR: `${BACKEND_BASE_URL}/api/v1/user/avatar`,
        LANGUAGE: `${BACKEND_BASE_URL}/api/v1/user/language`,
    },
};

export const ROUTES = {
    All: {
        HOME: '/',
    },
    CareerExplorer: {
        HOME: '/careerxplorer',
        CAREER_ONBOARDING: '/careerxplorer/onboarding/',
        CAREER_DETAILS: '/careerxplorer/:careerSlug',
        CAREER_SUBCATEGORY:(category: number) => `/careerxplorer/category/${category}`,
        CAREER_SUBCATEGORY_URL:`/careerxplorer/category/:category`,
        CAREER_LIST:(category:number, field: number) => `/careerxplorer/category/${category}/field/${field}/careers`,
        CAREER_LIST_URL:`/careerxplorer/category/:category/field/:field/careers`,
        CAREER_DETAILS_SLUG: (careerSlug: string) => `/careerxplorer/${careerSlug}`,
        SEARCH: '/careers',
    },
    Teach : '/teach',
    Discussions: {
    },
    Explore: {
        HOME: '/explore',
    },
    Subjects: '/subjects',
    Subject: {
        HOME: '/subject/:subjectSlug',
        SUBJECT_SLUG: (subjectSlug: string) => `/subject/${subjectSlug}`,
        CONTENT: '/subject/:subjectSlug/:contentType',
        CONTENT_SLUG: (subjectSlug: string, contentType: string) => `/subject/${subjectSlug}/${contentType}`,
    },
    Topic: '/topic/:topicSlug',
    CurriculumsPage: '/curricula',
    CurriculumModule: '/c/:curriculumSlug/:subjectSlug/:gradeSlug/:moduleSlug',
    Filters: {
        SEARCH: '/search',
    },
    General: {
        ABOUT: '/about',
        FAQ: '/faq',
        LOG_IN: '/log-in',
        SIGN_IN: '/sign-in',
        SIGN_UP: '/sign-up',
        LX_SIGN_IN: '/lx-sign-in',
        LX_SIGN_UP: '/lx-sign-up',
        SSO_AUTH_PAGE: '/sso-auth',
        PASSWORD_AUTH_PAGE: '/password-auth',
        EMAIL_CONFIRMATION: '/email-confirmation',
        RESET_PASSWORD: '/reset-password',
        RESET_PASSWORD_EMAIL: '/reset-password-email',
        ONBOARDING: '/onboarding',
        SIGNUP_DENIED_SDN: '/denied/sdn',
        SIGNUP_DENIED_AGE: '/denied/age',
        ROLE_SELECTION: '/role-selection',
        DENIED_BLOCKED: '/denied/blocked',
        DENIED_COUNTRY: '/denied/country',

        ACCESSIBILITY: 'https://accessibility.huit.harvard.edu/digital-accessibility-policy',
        ABOUT_SITE: 'https://about.labxchange.org',
        BLOG: 'https://about.labxchange.org/blog',
        EVENTS: 'https://about.labxchange.org/events',
        FAQ_SITE: 'https://labxchange.zendesk.com/hc/en-us/',
        CONTACT_US: 'https://labxchange.zendesk.com/hc/en-us/requests/new',
        HELP_CENTER: 'https://labxchange.zendesk.com/hc/en-us',
        NEWS: 'https://about.labxchange.org/news/',
        TEAM: 'https://about.labxchange.org/collaborate/',
        TUTORIALS: 'https://labxchange.zendesk.com/hc/en-us/categories/360002866654-Video-Resources',
        GETTING_STARTED: 'https://about.labxchange.org/get-started',
        TEACHER_RESOURCES: 'https://about.labxchange.org/teacher-resources',
        SHARE_STORY: 'https://harvard.az1.qualtrics.com/jfe/form/SV_1RjcrgwuqOYVnhz',
        OPEN_EDX: 'https://open.edx.org/',
    },
    Library: {
        HOME: '/library',
        SEARCH_TITLE: '/library_titles',
        SEARCH_POPULAR_QUERIES: '/library_popular_queries',
        BOOK: '/library/books/:itemKey',
        BOOK_SLUG: (itemKey: string) => `/library/books/${itemKey}`,
        CLUSTER: `/library/clusters/:clusterKey`,
        CLUSTER_SLUG: (clusterKey: string) => `/library/clusters/${clusterKey}`,
        PATHWAY: '/library/pathway/:pathwayKey',
        PATHWAY_EDIT: '/library/pathway/:pathwayKey/edit',
        PATHWAY_EDIT_SLUG: (pathwayKey: string) => `/library/pathway/${pathwayKey}/edit`,
        PATHWAY_ITEM: '/library/pathway/:pathwayKey/items/:itemKey/:relId?',
        PATHWAY_ITEM_ALT: '/library/items/:pathwayItemKey(lx-pb:[^/]+)',
        PATHWAY_ITEM_SLUG: (pathwayKey: string, itemKey: string, relId?: string) => (
            relId ? `/library/pathway/${pathwayKey}/items/${itemKey}/${relId}`
                  : `/library/pathway/${pathwayKey}/items/${itemKey}`
        ),
        PATHWAY_NEW: '/library/pathway/new',
        PATHWAY_SLUG: (pathwayKey: string) => `/library/pathway/${pathwayKey}`,
        ITEM: '/library/items/:itemKey',
        ITEM_SLUG: (itemKey: string) => `/library/items/${itemKey}`,
        ITEM_EDIT: '/library/items/:itemKey/edit',
        ITEM_EDIT_SLUG: (itemKey: string) => `/library/items/${itemKey}/edit`,
        ITEM_EDIT_OLX: '/library/items/:itemKey/edit/olx',
        ITEM_EDIT_OLX_SLUG: (itemKey: string) => `/library/items/${itemKey}/edit/olx`,
        ITEM_NEW: '/library/items/new/:itemType/:itemSubType?',
        ITEM_NEW_SLUG: (itemType: string, itemSubType?: string) => `/library/items/new/${itemType}/${itemSubType}`,
    },
    Messages: {
        HOME: '/dashboard/messages',
        MESSAGE_NEW: '/dashboard/messages/new/',
        MESSAGE: '/dashboard/messages/:conversation',
        MESSAGE_SLUG: (conversationId: string) => `/dashboard/messages/${conversationId}`,
    },
    People: {
        HOME: '/people',
    },
    Personal: {
        NOTIFICATIONS: '/notifications',
        PROFILE_SETTINGS: '/account-settings',
    },
    Community: {
        HOME: '/community',
        MENTEES: '/community/mentees',
        MENTORS: '/community/mentors',
        DISCUSSIONS: '/community/discussions',
        CATEGORY_ROUTE: '/community/discussions/:category',
        CATEGORY_ROUTE_SLUG: (category: string) => `/community/discussions/${category}`,
        SUBCATEGORY_ROUTE: '/community/discussions/c/:subCategory/',
        SUBCATEGORY_ROUTE_SLUG: (subCategoryId: string) => `/community/discussions/c/${subCategoryId}/`,
        SUBCATEGORY_THREAD_ROUTE: '/community/discussions/c/:subCategory/t/:thread/',
        SUBCATEGORY_THREAD_ROUTE_SLUG: (subCategoryId: string, threadId: string) => `/community/discussions/c/${subCategoryId}/t/${threadId}/`,
    },
    Dashboard: {
        HOME: '/dashboard',
        FAVORITES: '/dashboard/favorites',
        LEARNER_CLASSROOMS: '/dashboard/classes',
        EDUCATOR_CLASSROOMS: '/dashboard/educator/classes',
        EDUCATOR: '/dashboard/educator',
        LEARNER_PROGRESS: '/dashboard/learner/progress',
        ORGANIZATION_CONTENT: '/dashboard/org/:slug',
        ORGANIZATION_CONTENT_SLUG: (slug: string) => `/dashboard/org/${slug}`,
        ORGANIZATION_PROFILE_ADMIN: '/dashboard/org/:slug/profile',
        ORGANIZATION_PROFILE_ADMIN_SLUG: (slug: string) => `/dashboard/org/${slug}/profile`,
        ORGANIZATION_PEOPLE_ADMIN: '/dashboard/org/:slug/people',
        ORGANIZATION_PEOPLE_ADMIN_SLUG: (slug: string) => `/dashboard/org/${slug}/people`,
        ORGANIZATION_SOURCES_ADMIN: '/dashboard/org/:slug/sources',
        ORGANIZATION_SOURCES_ADMIN_SLUG: (slug: string) => `/dashboard/org/${slug}/sources`,
    },
    Classrooms: {
        CLASSROOM: '/classes/:classroomKey',
        CLASSROOM_SLUG: (classroomKey: string) => `/classes/${classroomKey}`,
        CLASSROOM_LEARNERS: '/classes/:classroomKey/learners',
        CLASSROOM_LEARNERS_SLUG: (classroomKey: string) => `/classes/${classroomKey}/learners`,
        CLASSROOM_PROGRESS: '/classes/:classroomKey/progress',
        CLASSROOM_PROGRESS_SLUG: (classroomKey: string) => `/classes/${classroomKey}/progress`,
        CLASSROOM_DISCUSSION: '/classes/:classroomKey/discussion',
        CLASSROOM_DISCUSSION_SLUG: (classroomKey: string) => `/classes/${classroomKey}/discussion`,
        CLASSROOM_DISCUSSION_THREAD: '/classes/:classroomKey/discussion/:thread',
        CLASSROOM_DISCUSSION_THREAD_SLUG: (classroomKey: string, thread: number) =>
            `/classes/${classroomKey}/discussion/${thread}`,
        CLASSROOM_DISCUSSION_THREAD_POST: '/classes/:classroomKey/discussion/:thread/:post',
        CLASSROOM_DISCUSSION_THREAD_POST_SLUG: (classroomKey: string, thread: number, post: number) =>
            `/classes/${classroomKey}/discussion/${thread}/${post}`,
    },
    Users : {
        PROFILE: '/u/:username',
        PROFILE_EDIT: '/edit/u/:username',
        PROFILE_EDIT_SLUG: (username: string) => `/edit/u/${username}`,
        PROFILE_SLUG: (username: string) => `/u/${username}`,
    },
    Organizations: {
        HOME: '/organizations',
        PROFILE: '/org/:slug',
        PROFILE_SLUG: (slug: string, contentType?: 'cluster'|'pathway'|'content') =>
            `/org/${slug}${contentType ? '/' + contentType : ''}`,
        PROFILE_FILTERED: '/org/:slug/:contentType',
        COLLABORATORS: '/collaborators',
    },
    Redirects: {
        ABE_CLUSTER_CUSTOM: '/abecluster',
        REDIRECT: '/s/:key',
        LOGIN_COMPLETE: '/login/complete',
    },
    Test: {
        UI_DEMOS: '/ui-demos',
    },
    Initiatives: {
        ALL: '/:key',
    },
    TagContent: {
        HOME: '/tag-content',
    }
};
